import { generatePageCards } from "@/_components/Card"
import CustomButton from "@/_components/CustomButton"
import FormInputExtended from "@/_components/FormInputExtended"
import LayoutCard, { genericCardExists } from "@/_components/LayoutCard"
import { setEntityFieldProps } from "@/_services/entity"
import { loc } from "@/_services/localization"
import { addOops } from "@/_services/notification"
import { executeLayout } from "@/_services/scripts"
import { customEvents, generateRows } from "@/_services/utils"
import axios from "axios"
import { checkErrors, getEntityServerRoute, lowerCaseFirstLetter } from "basikon-common-utils"
import React from "react"
import { Modal } from "react-bootstrap"

class EntityQuickAddModal extends React.Component {
  constructor(props) {
    super(props)

    const { entityName, entityType } = props

    if (!entityName) {
      console.error("Missing mandatory 'entityName' prop in <EntityQuickAddModal/>")
      this.state = {}
      return
    }

    const entityVariableName = lowerCaseFirstLetter(entityName) // CreditLine => creditLine

    const entity = {}
    if (entityType) entity.type = entityType

    this.state = {
      loading: true,
      entity,
      serverRoute: getEntityServerRoute(entityName),
      layout: { cache: {} },
      layoutScriptName: `${entityVariableName}-layout`, // CreditLine => "creditLine-layout"
      entityVariableName,
      prevEntityVariableName: `prev${entityName}`, // CreditLine => prevCreditLine
      // For now we don't render this array although it is well filled by checkErrors or the save catch block
      // because for some weird reason they don't always fit the expected format.
      errors: [],
    }
  }

  componentDidMount = async () => {
    const { entity } = this.state
    const statePatch = { loading: false }
    try {
      const layout = await this.executeLayout({ entity })
      statePatch.layout = layout
      statePatch.entity = entity
    } catch (error) {
      console.error("Error executing layout", error)
    }
    this.setState(statePatch)
  }

  executeLayout = async ({ entity, prevEntity }) => {
    const { layout: prevLayout, layoutScriptName, entityVariableName, prevEntityVariableName } = this.state

    const layout = await executeLayout({
      prevLayout,
      layoutScriptName,
      inQuickAddModal: true,
      entityType: entity?.type,
      [entityVariableName]: entity,
      [prevEntityVariableName]: prevEntity,
    })
    return layout
  }

  getField = col => {
    const { name, colProps, formInputProps, hidden } = col
    if (hidden || !name) return

    return <FormInputExtended key={name} field={name} colProps={colProps ?? { xs: 12 }} {...formInputProps} />
  }

  handleSetEntityState = async patch => {
    if (!patch) return

    const { entity: prevEntity } = this.state
    const entity = { ...prevEntity, ...patch }

    const layout = await this.executeLayout({ entity, prevEntity })
    this.setState({ entity, layout })
  }

  handleSave = async () => {
    const { onClose, entityName } = this.props
    const { entity, serverRoute } = this.state

    const errors = checkErrors(entity, patch => this.setState(patch), { skipWorkflowErrors: true })
    if (errors) {
      window.dispatchEvent(new CustomEvent(customEvents.formInput.forceComponentUpdate, { detail: { errors } }))
      return
    }

    try {
      const savedEntity = (await axios.post(serverRoute, entity)).data
      if (onClose) onClose(savedEntity)
    } catch (error) {
      const errors = error?.response?.data?.errors || []
      const fieldProps = error?.response?.data?.fieldProps
      const hasFieldProps = fieldProps && Object.keys(fieldProps).length > 0

      if (errors.length || hasFieldProps) {
        if (hasFieldProps) setEntityFieldProps(entity, fieldProps)

        this.setState({ entity, errors })
      } else {
        const errorMessage = error?.response?.data?.message || error.message || loc`Error saving` + ` ${entityName?.toLowerCase() || ""}`
        this.setState({ errors: [errorMessage] })
        addOops(error)
        if (error.stack) console.error("EntityWorkflowCard.handleSave", error.stack)
      }
    }
  }

  getCardComponent = ({ card, layout }) => {
    const { entityVariableName, entityName, serverRoute, entity } = this.state
    const { card: cardName, props = {} } = card

    if (genericCardExists(cardName)) {
      return (
        <LayoutCard
          card={card}
          entity={entity}
          layout={layout}
          entityName={entityName}
          serverRoute={serverRoute}
          onSetState={this.handleSetEntityState}
          handlers={{ [entityVariableName]: { obj: entity, onSetState: this.handleSetEntityState } }}
          {...props}
        />
      )
    }
  }

  render() {
    const { layout, entity, loading } = this.state
    const { title = "Add", saveButtonLabel = "Save", cancelButtonLabel = "Cancel", rows, onClose } = this.props

    return (
      <Modal show={true} onHide={() => onClose()} bsSize="large" backdrop="static" className="person-quick-add-modal">
        <Modal.Header closeButton>
          <Modal.Title>{loc(title)}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loading ? (
            <div className="text-center">
              <i className="icn-circle-notch icn-sm icn-spin text-gray" />
            </div>
          ) : rows?.length > 0 ? (
            generateRows({ getField: this.getField, rows, entity, handleSetState: this.handleSetEntityState })
          ) : (
            generatePageCards({ layout, getCardComponent: this.getCardComponent })
          )}
        </Modal.Body>

        <Modal.Footer>
          <CustomButton bsSize="sm" onClick={() => onClose()} label={cancelButtonLabel} />
          <CustomButton bsStyle="primary" bsSize="sm" fill onClick={() => this.handleSave(entity)} label={saveButtonLabel} />
        </Modal.Footer>
      </Modal>
    )
  }
}

export default EntityQuickAddModal
